import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Wizard, WizardField, WizardSection, ScopePicker, type WizardStep } from "../../wizard/Wizard";
import { ThreatStepBody, type ThreatTo } from "../../wizard/ThreatStep";
import { ResolutionPanel } from "../../shell/ResolutionPanel";
import { AssignAsTaskButton } from "../../shell/AssignAsTaskButton";

import { ApiError } from "../../api/client";
import { useMarkSeen } from "../../notifications/useMarkSeen";
import { HierarchyApi } from "../../admin/hierarchy/api";
import {
  IntelApi,
  type Classification,
  type IntelItem,
  type IntelKind,
  type IntelSeverity,
} from "./api";

export function IntelFeed() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [severity, setSeverity] = useState<IntelSeverity | "">("");
  const [kind, setKind] = useState<IntelKind | "">("");
  // Allow the sidebar's "Capture Intel" link to open the publish wizard
  // straight away by appending ?capture=1.
  const [showPublish, setShowPublish] = useState(searchParams.get("capture") === "1");

  const feed = useQuery({
    queryKey: ["intel", severity, kind],
    queryFn: () =>
      IntelApi.list({
        severity: (severity || undefined) as IntelSeverity | undefined,
        kind: (kind || undefined) as IntelKind | undefined,
      }),
    refetchInterval: 15_000,            // near-realtime polling
    refetchOnWindowFocus: true,
  });

  const redact = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => IntelApi.redact(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["intel"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  // Mark this category seen on mount + when fresh data lands. Decrements
  // the sidebar badge after the user has actually read the page.
  useMarkSeen("intel", feed.dataUpdatedAt);

  const items = (feed.data?.items ?? [])
    .slice()
    .sort((a, b) => (b.publishedAt ?? "").localeCompare(a.publishedAt ?? ""));

  // When Operations → Capture Intel sends us in with ?capture=1, the page is
  // taken over by the wizard — no top header (the wizard renders its own).
  const captureMode = searchParams.get("capture") === "1" && showPublish;

  return (
    <div>
      {!captureMode && (
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Intel feed</h1>
            <p className="muted" style={{ margin: 0, fontSize: 14 }}>
              Captures published by others in your scope. New items appear automatically.
              Use Operations → <strong>Capture Intel</strong> to publish your own.
            </p>
          </div>
          <button onClick={() => setShowPublish((v) => !v)}>
            {showPublish ? "Cancel" : "Capture"}
          </button>
        </header>
      )}

      {error && <div className="error">{error}</div>}
      {showPublish && <PublishForm onPublished={() => { setShowPublish(false); qc.invalidateQueries({ queryKey: ["intel"] }); }} />}

      {/* When Operations → Capture Intel sends us here with ?capture=1, hide
          the feed list. The page is purely the wizard until the user dismisses it. */}
      {searchParams.get("capture") === "1" && showPublish ? null : (
      <>
      <div className="row-3" style={{ marginTop: 16 }}>
        <label>
          Severity:
          <select value={severity} onChange={(e) => setSeverity(e.target.value as IntelSeverity | "")}>
            <option value="">All</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>
        <label>
          Kind:
          <select value={kind} onChange={(e) => setKind(e.target.value as IntelKind | "")}>
            <option value="">All</option>
            <option value="alert">Alert</option>
            <option value="observation">Observation</option>
            <option value="note">Note</option>
          </select>
        </label>
        <span />
      </div>

      {feed.isLoading && <p>Loading…</p>}
      {feed.error && <p className="error">{(feed.error as Error).message}</p>}

      <div className="card-grid">
        {items.map((i) => (
          <IntelCard
            key={i.id}
            item={i}
            onRedact={(reason) => redact.mutate({ id: i.id, reason })}
          />
        ))}
        {items.length === 0 && !feed.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>Nothing in your scope yet.</div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

function IntelCard({ item, onRedact }: { item: IntelItem; onRedact: (reason: string) => void }) {
  const [open, setOpen] = useState(false);
  const [showRedact, setShowRedact] = useState(false);
  const [reason, setReason] = useState("");
  const isRedacted = !!item.redactedAt;

  return (
    <>
      <div className={`list-card sev-${item.severity}${isRedacted ? " redacted" : ""}`} onClick={() => setOpen(true)} style={{ cursor: "pointer" }}>
        <div className="list-card-header">
          <SeverityBadge severity={item.severity} />
          <ClassificationBadge level={item.classification} />
          <span className="muted" style={{ fontSize: 11 }}>{item.kind}</span>
        </div>
        <div className="list-card-title">{item.title}</div>
        <div className="list-card-snippet">
          {isRedacted ? `[REDACTED — ${item.redactionReason ?? "no reason given"}]` : item.body}
        </div>
        <div className="list-card-foot">
          <span>{relativeTime(item.publishedAt)}</span>
          <span>{isRedacted ? "redacted" : "active"}</span>
        </div>
      </div>
      {open && (
        <div role="dialog" aria-modal="true"
          onClick={(e) => { if (e.currentTarget === e.target) setOpen(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16,
          }}>
          <div className="card" style={{ maxWidth: 640, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <div style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
                  <SeverityBadge severity={item.severity} />
                  <ClassificationBadge level={item.classification} />
                  <span className="muted" style={{ fontSize: 11 }}>{item.kind}</span>
                </div>
                <h2 style={{ margin: 0, fontSize: 18 }}>{item.title}</h2>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                  {relativeTime(item.publishedAt)}
                </div>
              </div>
              <button className="ghost" onClick={() => setOpen(false)}>Close</button>
            </div>
            <div style={{ marginTop: 16, whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5 }}>
              {isRedacted ? `[REDACTED]\nReason: ${item.redactionReason ?? "—"}` : item.body}
            </div>
            {!isRedacted && (
              <>
                <ResolutionPanel
                  collection="intelItems"
                  recordId={item.id}
                  current={(item as { resolutionStatus?: "pending" | "in_progress" | "resolved" | "false_alarm" }).resolutionStatus}
                />
                <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <AssignAsTaskButton
                    relatedType="intel"
                    relatedId={item.id}
                    suggestedTitle={`Action intel: ${item.title}`}
                  />
                  {showRedact ? (
                    <div style={{ display: "flex", gap: 8, flex: 1 }}>
                      <input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Reason for redaction"
                        style={{ flex: 1 }}
                      />
                      <button
                        className="danger"
                        disabled={!reason}
                        onClick={() => {
                          if (window.confirm(`Redact this item? Reason saved: "${reason}". Cannot be undone.`)) {
                            onRedact(reason); setShowRedact(false); setReason(""); setOpen(false);
                          }
                        }}
                      >Confirm redact</button>
                      <button className="ghost" onClick={() => setShowRedact(false)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="ghost" onClick={() => setShowRedact(true)}>Redact</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}


function PublishForm({ onPublished }: { onPublished: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [kind, setKind] = useState<IntelKind>("note");
  const [severity, setSeverity] = useState<IntelSeverity>("low");
  const [classification, setClassification] = useState<Classification>("unclassified");
  const [threatTo, setThreatTo] = useState<ThreatTo>("none");
  const [threatTarget, setThreatTarget] = useState("");
  const [threatDetails, setThreatDetails] = useState("");

  const scope = useQuery({ queryKey: ["my-scope"], queryFn: HierarchyApi.myScope });
  const [cid, setCid] = useState("");
  const [wid, setWid] = useState("");
  const [bid, setBid] = useState("");
  const constituencies = scope.data?.constituencies ?? [];
  const wardsForCid = (scope.data?.wards ?? []).filter((w) => w.constituencyId === cid);
  const branchesForWid = (scope.data?.branches ?? []).filter((b) => b.wardId === wid);
  const noConstituencies = !scope.isLoading && constituencies.length === 0;

  const publish = useMutation({
    mutationFn: IntelApi.publish,
    onSuccess: onPublished,
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  function submit() {
    if (!title || !body || !bid) return;
    publish.mutate({
      title, body, branchId: bid, kind, severity, classification,
      threatTo,
      threatTarget: threatTarget || undefined,
      threatDetails: threatDetails || undefined,
    });
  }

  if (noConstituencies) {
    return (
      <div className="info">
        You don't have any constituencies in your scope yet. A superadmin needs to either create one in
        <a href="/app/admin/hierarchy"> Hierarchy</a> or assign you to one via
        <a href="/app/admin/users"> Users → Edit access</a>.
      </div>
    );
  }

  const steps: WizardStep[] = [
    {
      title: "Where",
      canNext: !!bid,
      blockerHint: "Pick a constituency, ward and branch to continue",
      body: (
        <WizardSection title="Where did you observe this?" hint="Captures attach to a single branch — pick the one this observation belongs to.">
          <ScopePicker
            constituencies={constituencies}
            wards={wardsForCid}
            branches={branchesForWid}
            cid={cid} wid={wid} bid={bid}
            onCidChange={(v) => { setCid(v); setWid(""); setBid(""); }}
            onWidChange={(v) => { setWid(v); setBid(""); }}
            onBidChange={setBid}
          />
        </WizardSection>
      ),
    },
    {
      title: "What",
      canNext: !!title.trim() && !!body.trim(),
      blockerHint: "Title and body are required",
      body: (
        <WizardSection title="What did you see?" hint="A short title plus enough detail that a senior can act on it.">
          <WizardField label="Title">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Unscheduled rally near Ward 7" />
          </WizardField>
          <WizardField label="Kind">
            <select value={kind} onChange={(e) => setKind(e.target.value as IntelKind)}>
              <option value="note">Note — general observation, no action needed</option>
              <option value="observation">Observation — pattern or fact worth noting</option>
              <option value="alert">Alert — needs attention soon</option>
            </select>
          </WizardField>
          <WizardField label="Body">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Who, what, when, where. Don't speculate." />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Threat",
      canNext: threatTo === "none" || !!threatTarget.trim(),
      blockerHint: "Capture who is being threatened, or set 'Not a threat'.",
      body: (
        <ThreatStepBody
          threatTo={threatTo}
          threatTarget={threatTarget}
          threatDetails={threatDetails}
          onThreatTo={setThreatTo}
          onThreatTarget={setThreatTarget}
          onThreatDetails={setThreatDetails}
        />
      ),
    },
    {
      title: "Classification",
      canNext: true,
      body: (
        <WizardSection title="How sensitive is this?" hint="Severity drives urgency; classification drives who can read it.">
          <WizardField label="Severity" hint="Critical and High push notifications immediately to the chain of command.">
            <select value={severity} onChange={(e) => setSeverity(e.target.value as IntelSeverity)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </WizardField>
          <WizardField label="Classification" hint="Restricted/Secret items are hidden from anyone without case.read_classified.">
            <select value={classification} onChange={(e) => setClassification(e.target.value as Classification)}>
              <option value="unclassified">Unclassified</option>
              <option value="restricted">Restricted</option>
              <option value="secret">Secret</option>
            </select>
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Review",
      canNext: !!title.trim() && !!body.trim() && !!bid,
      body: (
        <WizardSection title="Review and capture" hint="Once you submit, the capture flows upward — you won't see it again in your feed.">
          <dl className="kv">
            <dt>Title</dt><dd>{title}</dd>
            <dt>Kind</dt><dd>{kind}</dd>
            <dt>Severity</dt><dd>{severity}</dd>
            <dt>Classification</dt><dd>{classification}</dd>
            <dt>Branch</dt><dd>{branchesForWid.find((b) => b.id === bid)?.name ?? bid}</dd>
            <dt>Body</dt><dd style={{ whiteSpace: "pre-wrap" }}>{body}</dd>
          </dl>
        </WizardSection>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: 16 }}>
      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      <Wizard
        title="Capture intel"
        steps={steps}
        busy={publish.isPending}
        submitLabel="Capture"
        onSubmit={submit}
        onCancel={onPublished}
      />
    </div>
  );
}

function SeverityBadge({ severity }: { severity: IntelSeverity }) {
  const colors: Record<IntelSeverity, string> = {
    low: "#3a8053",
    medium: "#a87b1c",
    high: "#c0531a",
    critical: "#b14444",
  };
  return (
    <span style={{
      fontSize: 11,
      padding: "2px 6px",
      background: colors[severity],
      color: "white",
      borderRadius: 4,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    }}>{severity}</span>
  );
}

function ClassificationBadge({ level }: { level: Classification }) {
  if (level === "unclassified") return null;
  const color = level === "secret" ? "#b14444" : "#a87b1c";
  return (
    <span style={{
      fontSize: 11,
      padding: "2px 6px",
      background: color,
      color: "white",
      borderRadius: 4,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    }}>{level}</span>
  );
}

function relativeTime(iso: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const seconds = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
